import { format, addHours, subHours } from 'date-fns';
import { formatInTimeZone, toZonedTime, fromZonedTime } from 'date-fns-tz';
import { getNow } from './clock';

// GMT+6 / UTC+6 timezone constant
export const GMT_PLUS_6 = 'Asia/Dhaka';

/**
 * Convert UTC date to local timezone (GMT+6)
 */
export const utcToLocal = (utcDate: Date | string): Date => {
  const date = typeof utcDate === 'string' ? new Date(utcDate) : utcDate;
  return toZonedTime(date, GMT_PLUS_6);
};

/**
 * Convert local timezone (GMT+6) date to UTC
 */
export const localToUtc = (localDate: Date): Date => {
  return fromZonedTime(localDate, GMT_PLUS_6);
};

/**
 * Format date in local timezone (GMT+6)
 */
export const formatInLocalTime = (date: Date | string, formatStr: string = 'PPP p'): string => {
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    
    // Check if date is valid
    if (isNaN(dateObj.getTime())) {
      console.error('Invalid date provided to formatInLocalTime:', date);
      return 'Invalid date';
    }
    
    return formatInTimeZone(dateObj, GMT_PLUS_6, formatStr);
  } catch (error) {
    console.error('Error formatting date in local time:', error, date);
    return 'Invalid date';
  }
};

/**
 * Format date in any timezone with error handling
 */
export const formatInTimezone = (date: Date | string, timezone: string, formatStr: string = 'PPP p'): string => {
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    
    // Check if date is valid
    if (isNaN(dateObj.getTime())) {
      console.error('Invalid date provided to formatInTimezone:', date);
      return 'Invalid date';
    }
    
    return formatInTimeZone(dateObj, timezone, formatStr);
  } catch (error) {
    console.error('Error formatting date in timezone:', error, { date, timezone, formatStr });
    return 'Invalid date';
  }
};

/**
 * Get current date/time in GMT+6
 */
export const nowInLocal = (): Date => {
  return toZonedTime(getNow(), GMT_PLUS_6);
};

/**
 * Check if a date is today in local timezone
 */
export const isTodayInLocal = (date: Date | string): boolean => {
  const today = nowInLocal();
  const checkDate = typeof date === 'string' ? utcToLocal(date) : utcToLocal(date.toISOString());
  
  return (
    today.getFullYear() === checkDate.getFullYear() &&
    today.getMonth() === checkDate.getMonth() &&
    today.getDate() === checkDate.getDate()
  );
};

/**
 * Check if a date is tomorrow in local timezone
 */
export const isTomorrowInLocal = (date: Date | string): boolean => {
  const tomorrow = addHours(nowInLocal(), 24);
  const checkDate = typeof date === 'string' ? utcToLocal(date) : utcToLocal(date.toISOString());
  
  return (
    tomorrow.getFullYear() === checkDate.getFullYear() &&
    tomorrow.getMonth() === checkDate.getMonth() &&
    tomorrow.getDate() === checkDate.getDate()
  );
};

/**
 * Check if two dates are the same day in local timezone
 */
export const isSameDayInLocal = (date1: Date | string, date2: Date | string): boolean => {
  const localDate1 = typeof date1 === 'string' ? utcToLocal(date1) : utcToLocal(date1.toISOString());
  const localDate2 = typeof date2 === 'string' ? utcToLocal(date2) : utcToLocal(date2.toISOString());
  
  return (
    localDate1.getFullYear() === localDate2.getFullYear() &&
    localDate1.getMonth() === localDate2.getMonth() &&
    localDate1.getDate() === localDate2.getDate()
  );
};

/**
 * Create a date object for today in local timezone at a specific time
 */
export const createLocalDateTime = (hours: number = 0, minutes: number = 0): Date => {
  const today = nowInLocal();
  today.setHours(hours, minutes, 0, 0);
  return today;
};

/**
 * Convert a date to ISO string format for API (always in UTC)
 */
export const toApiFormat = (localDate: Date): string => {
  return localToUtc(localDate).toISOString();
};

/**
 * Convert API date string to local timezone for display
 */
export const fromApiFormat = (apiDate: string): Date => {
  return utcToLocal(apiDate);
};

/**
 * Re-export fromZonedTime for timezone handling in components
 */
export { fromZonedTime };